import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const CLOSE_MS = 200;
/** How far the sheet is pulled down before letting go closes it. */
const DRAG_CLOSE_PX = 90;

/**
 * A sheet: slides up from the bottom on a phone (grab it and pull down to close),
 * a small centred dialog on a wider screen. Long content scrolls inside it, and
 * on a phone it rides above the keyboard (`--kb`, kept by the shell).
 */
export function Sheet({
  open,
  onClose,
  title,
  left,
  right,
  label,
  tall = false,
  closable = false,
  className,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** A header button on the left, usually Cancel. */
  left?: ReactNode;
  /** A header button on the right, usually Done or Save. */
  right?: ReactNode;
  /** The dialog's name when the title is not plain text. */
  label?: string;
  /** Opens to nearly the full height, for a list or a long form. */
  tall?: boolean;
  /** A close X in the head, for a sheet of the Season kit. */
  closable?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const [leaving, setLeaving] = useState(false);
  const [drag, setDrag] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const start = useRef<number | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const dismiss = useCallback(() => {
    setLeaving(true);
    window.setTimeout(() => {
      setLeaving(false);
      setDrag(0);
      closeRef.current();
    }, CLOSE_MS);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
    };
    document.addEventListener("keydown", onKey);
    box.current?.focus({ preventScroll: true });
    return () => document.removeEventListener("keydown", onKey);
  }, [open, dismiss]);

  if (!open) return null;

  const onPointerDown = (e: React.PointerEvent) => {
    start.current = e.clientY;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (start.current === null) return;
    setDrag(Math.max(0, e.clientY - start.current));
  };
  const onPointerUp = () => {
    if (start.current === null) return;
    start.current = null;
    if (drag > DRAG_CLOSE_PX) dismiss();
    else setDrag(0);
  };

  const cls = ["ph-sheet", tall ? "tall" : "", leaving ? "leaving" : "", drag ? "dragging" : "", className ?? ""]
    .filter(Boolean)
    .join(" ");

  return createPortal(
    <div
      className={leaving ? "ph-sheet-scrim leaving" : "ph-sheet-scrim"}
      onClick={(e) => {
        if (e.target === e.currentTarget) dismiss();
      }}
    >
      <div
        className={cls}
        role="dialog"
        aria-modal="true"
        aria-label={label ?? (typeof title === "string" ? title : undefined)}
        tabIndex={-1}
        ref={box}
        style={drag ? { transform: `translateY(${drag}px)` } : undefined}
      >
        <div
          className="ph-sheet-grab"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <i />
        </div>
        {title || left || right || closable ? (
          <div className="ph-sheet-head">
            <span className="l">{left}</span>
            {title ? <h2>{title}</h2> : <span />}
            <span className="r">
              {right ??
                (closable ? (
                  <button type="button" className="ph-sheet-x" aria-label="Close" onClick={dismiss}>
                    <svg className="i" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                  </button>
                ) : null)}
            </span>
          </div>
        ) : null}
        <div className="ph-sheet-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

interface SheetAction {
  label: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
}

/**
 * The phone's own menu: a list of actions in a sheet, with Cancel apart at the
 * bottom. Picking one closes the sheet first, then runs it.
 */
export function ActionSheet({
  open,
  onClose,
  title,
  message,
  actions,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  message?: ReactNode;
  actions: SheetAction[];
}) {
  return (
    <Sheet open={open} onClose={onClose} label={typeof title === "string" ? title : "Actions"} className="ph-actions">
      {title || message ? (
        <div className="ph-actions-head">
          {title ? <b>{title}</b> : null}
          {message ? <p>{message}</p> : null}
        </div>
      ) : null}
      <div className="ph-grp">
        {actions.map((a, i) => (
          <button
            key={i}
            type="button"
            className={a.danger ? "ph-row ph-act danger" : "ph-row ph-act"}
            disabled={a.disabled}
            onClick={() => {
              onClose();
              a.onSelect();
            }}
          >
            {a.icon ? <span className="ph-ico plain">{a.icon}</span> : null}
            <span className="m">
              <span className="t">{a.label}</span>
            </span>
          </button>
        ))}
      </div>
      <div className="ph-grp">
        <button type="button" className="ph-row ph-act cancel" onClick={onClose}>
          <span className="m">
            <span className="t">Cancel</span>
          </span>
        </button>
      </div>
    </Sheet>
  );
}
